import React, { useCallback, useEffect, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Input, Modal, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import CustomSelect from '../../../components/forms/CustomSelect';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useCorporations from '../../../hooks/api-calls/useCorporations';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { FundaeGroupingService } from '../../../services/fundaeGrouping/fundaeGroupingService';

const columnOrder = ['id', 'name', 'registrationDate', 'organisingCorporation'];

const normalizeDateValue = (value: any): string => {
    if (!value) return '';
    const rawDate = typeof value === 'object' && value?.date ? value.date : value;
    return typeof rawDate === 'string' && rawDate.length >= 10 ? rawDate.slice(0, 10) : '';
};

const formatDateLabel = (value: any): string => {
    const normalized = normalizeDateValue(value);
    if (!normalized) return 'N/A';
    const [year, month, day] = normalized.split('-');
    if (!year || !month || !day) return 'N/A';
    return `${day}/${month}/${year}`;
};

const FundaeGroupingModalForm: React.FC<{
    data: { id?: string; name?: string; registrationDate?: string; organisingCorporationId?: string } | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}> = ({ data, isOpen, onClose, refetch }) => {
    const [name, setName] = useState('');
    const [registrationDate, setRegistrationDate] = useState('');
    const [organisingCorporationId, setOrganisingCorporationId] = useState('');
    const [loading, setLoading] = useState(false);
    const { handleErrors } = useHandleErrors();
    const { fetchCorporations, getCorporationsList } = useCorporations({ active: true, corporation_function: 'organising' });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setName(data?.name ?? '');
        setRegistrationDate(data?.registrationDate ?? '');
        setOrganisingCorporationId(data?.organisingCorporationId ?? '');
        fetchCorporations({});
    }, [isOpen, data]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name?.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!registrationDate) {
            toast.error('La fecha de alta es obligatoria');
            return;
        }
        if (!organisingCorporationId) {
            toast.error('La empresa organizadora es obligatoria');
            return;
        }
        setLoading(true);
        try {
            const service = new FundaeGroupingService();
            const response = data?.id
                ? await (await service.edit({
                    id: data.id,
                    name: name.trim(),
                    registrationDate,
                    organisingCorporationId
                })).getResponseData()
                : await (await service.create({
                    name: name.trim(),
                    registrationDate,
                    organisingCorporationId
                })).getResponseData();
            if (response?.success) {
                toast.success(data?.id ? 'Agrupación actualizada' : 'Agrupación creada');
                onClose();
                refetch();
            } else {
                handleErrors(response);
            }
        } catch {
            toast.error('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <h3 className="text-lg font-semibold">
                    {data?.id ? 'Editar Agrupación Fundae' : 'Crear Agrupación Fundae'}
                </h3>
                <Input
                    id="name"
                    label="Nombre"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <Input
                    id="registrationDate"
                    label="Fecha de alta"
                    type="date"
                    value={registrationDate}
                    onChange={(e) => setRegistrationDate(e.target.value)}
                />
                <CustomSelect
                    id="organisingCorporationId"
                    label="Empresa organizadora"
                    options={getCorporationsList()}
                    value={
                        organisingCorporationId
                            ? getCorporationsList().find((corporation: { value: string; label: string }) => corporation.value === organisingCorporationId) || null
                            : null
                    }
                    onChange={(value: any) => setOrganisingCorporationId(value?.value || '')}
                    placeholder="Selecciona organizadora"
                    isSearchable
                    isClearable
                />
                <div className="flex gap-2 justify-end pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancelar
                    </button>
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-primary text-white rounded">
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const FundaeGroupingsList = () => {
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const fundaeGroupingService = new FundaeGroupingService();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        const response = await fundaeGroupingService.list(filters);
        return response.getResponseData() as any;
    }, [filters]));

    const handleDelete = async (id: string) => {
        const response = await (await fundaeGroupingService.delete(id)).getResponseData();
        if (response?.success) {
            toast.success('Agrupación Fundae eliminada correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleEdit = useCallback((row: any) => {
        setSelectedItem({
            id: row.id,
            name: row.name,
            registrationDate: normalizeDateValue(row.registrationDate),
            organisingCorporationId: row.organisingCorporation?.id || '',
        });
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedItem(null);
    }, []);

    const orderedData = () => data?.data?.map((row: any) => ({
        id: row.id,
        name: row.name,
        registrationDate: row.registrationDate,
        organisingCorporation: row.organisingCorporation,
    })) ?? [];

    return (
        <div className="space-y-6">
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id="fundae-groupings-table"
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'registrationDate',
                            columnNameKey: 'Fecha de alta',
                            render: (row: any) => formatDateLabel(row.row.original.registrationDate),
                        },
                        {
                            key: 'organisingCorporation',
                            columnNameKey: 'Empresa organizadora',
                            render: (row: any) => row.row.original.organisingCorporation?.name || 'N/A',
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'contracts', permission: 'admin_contracts' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Editar" placement="top" color="invert">
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                        onClick={() => handleEdit(row)}
                                    >
                                        <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'contracts', permission: 'admin_contracts' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                    <div>
                                        <DeletePopover
                                            title="Eliminar agrupación Fundae"
                                            description={`¿Estás seguro de que deseas eliminar "${row.name}"?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            ),
                        },
                    ]}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage ?? 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                >
                    {userCan('admin_contracts', 'contracts') && (
                        <Button onClick={() => { setSelectedItem(null); setIsModalOpen(true); }}>
                            Crear agrupación Fundae
                        </Button>
                    )}
                </CustomTable>
            )}

            <FundaeGroupingModalForm
                data={selectedItem}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default FundaeGroupingsList;
